import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MandantData } from './MandantenList';
import './MandantForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AuthUser {
  name: string;
  email: string;
  rolle: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  firmenname: string | null;
  primaerfarbe: string | null;
}

interface MandantFormProps {
  authUser: AuthUser;
  mode: 'create' | 'edit';
  mandant?: MandantData;
  onSave: () => void;
  onClose: () => void;
}

const MandantForm: React.FC<MandantFormProps> = ({ authUser, mode, mandant, onSave, onClose }) => {
  const isEdit = mode === 'edit';
  const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };

  // Create-Modus Felder
  const [createForm, setCreateForm] = useState({
    slug: '',
    firmenname: '',
    plan: 'STANDARD' as 'STANDARD' | 'ENTERPRISE',
    max_benutzer: '',
    primaerfarbe: 'var(--green-900)',
    admin_email: '',
    admin_password: '',
    admin_name: '',
  });

  // Edit-Modus Felder
  const [editForm, setEditForm] = useState({
    firmenname: '',
    adresse: '',
    ust_id: '',
    plan: 'STANDARD' as 'STANDARD' | 'ENTERPRISE',
    max_benutzer: '',
    primaerfarbe: 'var(--green-900)',
    // E-Rechnung / Zahlungsdaten (M3 / AP 3.2)
    strasse: '',
    plz: '',
    ort: '',
    land_code: 'DE',
    iban: '',
    bic: '',
    steuernummer: '',
    email_rechnung: '',
  });

  /** Felder aus der E-Rechnungs-Sektion – Reihenfolge = Anzeigereihenfolge. */
  const E_RECHNUNG_FELDER = [
    'strasse', 'plz', 'ort', 'land_code',
    'iban', 'bic', 'steuernummer', 'email_rechnung',
  ] as const;

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit && mandant) {
      setEditForm({
        firmenname: mandant.firmenname,
        adresse: mandant.adresse || '',
        ust_id: mandant.ust_id || '',
        plan: mandant.plan,
        max_benutzer: mandant.max_benutzer !== null ? String(mandant.max_benutzer) : '',
        primaerfarbe: mandant.primaerfarbe || 'var(--green-900)',
        strasse: mandant.strasse || '',
        plz: mandant.plz || '',
        ort: mandant.ort || '',
        land_code: mandant.land_code || 'DE',
        iban: mandant.iban || '',
        bic: mandant.bic || '',
        steuernummer: mandant.steuernummer || '',
        email_rechnung: mandant.email_rechnung || '',
      });
    }
  }, [isEdit, mandant]);

  const handleCreateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setCreateForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    setSaving(true);
    try {
      if (isEdit && mandant) {
        const payload: any = {
          firmenname: editForm.firmenname.trim(),
          plan: editForm.plan,
        };
        if (editForm.adresse.trim()) payload.adresse = editForm.adresse.trim();
        if (editForm.ust_id.trim()) payload.ust_id = editForm.ust_id.trim();
        if (editForm.max_benutzer) payload.max_benutzer = parseInt(editForm.max_benutzer, 10);
        if (editForm.primaerfarbe) payload.primaerfarbe = editForm.primaerfarbe;
        // E-Rechnungsfelder immer mitsenden (auch leer): sonst liesse sich eine
        // falsch erfasste IBAN nicht mehr loeschen.
        E_RECHNUNG_FELDER.forEach(feld => { payload[feld] = editForm[feld].trim(); });

        await axios.put(`${API_URL}/api/mandanten/${mandant.mandant_id}`, payload, { headers });
      } else {
        const payload: any = {
          slug: createForm.slug.trim(),
          firmenname: createForm.firmenname.trim(),
          plan: createForm.plan,
          admin_email: createForm.admin_email.trim(),
          admin_password: createForm.admin_password,
          admin_name: createForm.admin_name.trim(),
        };
        if (createForm.max_benutzer) payload.max_benutzer = parseInt(createForm.max_benutzer, 10);
        if (createForm.primaerfarbe) payload.primaerfarbe = createForm.primaerfarbe;

        await axios.post(`${API_URL}/api/mandanten`, payload, { headers });
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mandant-form-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mandant-form-modal">
        <div className="mandant-form-header">
          <h2 className="mandant-form-title">
            {isEdit ? `Mandant bearbeiten: ${mandant?.firmenname}` : 'Neuer Mandant'}
          </h2>
          <button className="btn-close-modal" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mandant-form">
          {!isEdit ? (
            <>
              <p className="form-section-title">Mandant-Stammdaten</p>

              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={createForm.slug}
                  onChange={handleCreateChange}
                  placeholder="z.B. muster-gmbh"
                  required
                />
                <span className="form-hint">Eindeutiger URL-Bezeichner, nur Kleinbuchstaben und Bindestriche.</span>
              </div>

              <div className="form-group">
                <label>Firmenname *</label>
                <input
                  type="text"
                  name="firmenname"
                  value={createForm.firmenname}
                  onChange={handleCreateChange}
                  placeholder="Muster GmbH"
                  required
                />
              </div>

              <div className="form-group">
                <label>Plan</label>
                <select name="plan" value={createForm.plan} onChange={handleCreateChange}>
                  <option value="STANDARD">STANDARD</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </div>

              <div className="form-group">
                <label>Max. Benutzer</label>
                <input
                  type="number"
                  name="max_benutzer"
                  value={createForm.max_benutzer}
                  onChange={handleCreateChange}
                  placeholder="Leer = unbegrenzt"
                  min={1}
                />
              </div>

              <div className="form-group">
                <label>Primärfarbe</label>
                <input
                  type="color"
                  name="primaerfarbe"
                  value={createForm.primaerfarbe}
                  onChange={handleCreateChange}
                />
              </div>

              <p className="form-section-title">Admin-Zugang</p>

              <div className="form-group">
                <label>Admin-Name *</label>
                <input
                  type="text"
                  name="admin_name"
                  value={createForm.admin_name}
                  onChange={handleCreateChange}
                  placeholder="Max Mustermann"
                  required
                />
              </div>

              <div className="form-group">
                <label>Admin-E-Mail *</label>
                <input
                  type="email"
                  name="admin_email"
                  value={createForm.admin_email}
                  onChange={handleCreateChange}
                  placeholder="admin@muster-gmbh.de"
                  required
                />
              </div>

              <div className="form-group">
                <label>Admin-Passwort *</label>
                <input
                  type="password"
                  name="admin_password"
                  value={createForm.admin_password}
                  onChange={handleCreateChange}
                  placeholder="Mindestens 8 Zeichen"
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Firmenname *</label>
                <input
                  type="text"
                  name="firmenname"
                  value={editForm.firmenname}
                  onChange={handleEditChange}
                  placeholder="Muster GmbH"
                  required
                />
              </div>

              <div className="form-group">
                <label>Adresse</label>
                <textarea
                  name="adresse"
                  value={editForm.adresse}
                  onChange={handleEditChange}
                  placeholder="Musterstraße 1, 12345 Musterstadt"
                />
              </div>

              <div className="form-group">
                <label>USt-ID</label>
                <input
                  type="text"
                  name="ust_id"
                  value={editForm.ust_id}
                  onChange={handleEditChange}
                  placeholder="DE123456789"
                />
              </div>

              <div className="form-group">
                <label>Plan</label>
                <select name="plan" value={editForm.plan} onChange={handleEditChange}>
                  <option value="STANDARD">STANDARD</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </div>

              <div className="form-group">
                <label>Max. Benutzer</label>
                <input
                  type="number"
                  name="max_benutzer"
                  value={editForm.max_benutzer}
                  onChange={handleEditChange}
                  placeholder="Leer = unbegrenzt"
                  min={1}
                />
              </div>

              <div className="form-group">
                <label>Primärfarbe</label>
                <input
                  type="color"
                  name="primaerfarbe"
                  value={editForm.primaerfarbe}
                  onChange={handleEditChange}
                />
              </div>

              <p className="form-section-title">E-Rechnung / Zahlungsdaten</p>
              <span className="form-hint">
                Pflicht, sobald für einen Kunden ZUGFeRD oder XRechnung aktiviert ist.
                Die Freitext-Adresse oben bleibt für den PDF-Briefkopf maßgeblich.
              </span>

              <div className="form-group">
                <label>Straße</label>
                <input
                  type="text"
                  name="strasse"
                  value={editForm.strasse}
                  onChange={handleEditChange}
                  placeholder="Industriestr. 42"
                />
              </div>

              <div className="form-group">
                <label>PLZ</label>
                <input
                  type="text"
                  name="plz"
                  value={editForm.plz}
                  onChange={handleEditChange}
                  placeholder="97070"
                />
              </div>

              <div className="form-group">
                <label>Ort</label>
                <input
                  type="text"
                  name="ort"
                  value={editForm.ort}
                  onChange={handleEditChange}
                  placeholder="Würzburg"
                />
              </div>

              <div className="form-group">
                <label>Ländercode</label>
                <input
                  type="text"
                  name="land_code"
                  maxLength={2}
                  value={editForm.land_code}
                  onChange={handleEditChange}
                  placeholder="DE"
                />
              </div>

              <div className="form-group">
                <label>IBAN</label>
                <input
                  type="text"
                  name="iban"
                  value={editForm.iban}
                  onChange={handleEditChange}
                  placeholder="DE89370400440532013000"
                />
              </div>

              <div className="form-group">
                <label>BIC</label>
                <input
                  type="text"
                  name="bic"
                  value={editForm.bic}
                  onChange={handleEditChange}
                  placeholder="COBADEFFXXX"
                />
              </div>

              <span className="form-hint">
                IBAN und BIC hier sind veraltete Felder und werden für die E-Rechnung
                nicht mehr verwendet. Maßgeblich ist die primäre Bankverbindung unter
                Einstellungen → Firma → Bankverbindungen.
              </span>

              <div className="form-group">
                <label>Steuernummer</label>
                <input
                  type="text"
                  name="steuernummer"
                  value={editForm.steuernummer}
                  onChange={handleEditChange}
                  placeholder="Alternative zur USt-ID"
                />
              </div>

              <div className="form-group">
                <label>E-Mail für Rechnungen</label>
                <input
                  type="email"
                  name="email_rechnung"
                  value={editForm.email_rechnung}
                  onChange={handleEditChange}
                  placeholder="rechnung@muster-gmbh.de"
                />
              </div>
            </>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving
                ? 'Wird gespeichert...'
                : isEdit ? 'Speichern' : 'Mandant anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MandantForm;
